/**
 * UpgradePromptModal — Modal for free tier users who hit quota limit.
 * LAUNCH:C6.P3
 */
import React, { useEffect, useRef } from 'react';
import { X, Zap, Check } from 'lucide-react';

const BENEFITS = [
  'Unlimited AI grades',
  '500 vault items',
  'Full convention intel',
  'Badges & notifications',
];

export default function UpgradePromptModal({ isOpen, onClose, currentUsed, limit }) {
  const modalRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      triggerRef.current = document.activeElement;
      // Focus first focusable element in modal
      /* c8 ignore start */
      const timer = setTimeout(() => {
        const focusable = modalRef.current?.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        focusable?.focus();
      }, 0);
      return () => clearTimeout(timer);
    } else if (triggerRef.current) {
      triggerRef.current.focus();
      triggerRef.current = null;
    }
    /* c8 ignore stop */
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }

      // Focus trap
      if (e.key === 'Tab' && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        /* c8 ignore next */
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        /* c8 ignore start */
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
        /* c8 ignore stop */
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />

      {/* Modal */}
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="upgrade-heading"
        className="relative bg-zinc-900 border border-zinc-700 rounded-xl p-6 max-w-sm w-full mx-4 shadow-2xl"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1 hover:bg-zinc-800 rounded transition-colors"
          aria-label="Close"
        >
          <X className="w-4 h-4 text-zinc-400" aria-hidden="true" />
        </button>

        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className="w-12 h-12 bg-orange-500/20 rounded-full flex items-center justify-center">
            <Zap className="w-6 h-6 text-orange-400" aria-hidden="true" />
          </div>
        </div>

        {/* Heading */}
        <h2
          id="upgrade-heading"
          className="text-lg font-bold text-white text-center mb-2"
        >
          You've used all {limit} free AI grades this month
        </h2>

        <p className="text-sm text-zinc-400 text-center mb-4">
          Upgrade to Collector tier for unlimited grades and more.
        </p>

        {/* Benefits */}
        <ul className="space-y-2 mb-4">
          {BENEFITS.map((benefit) => (
            <li key={benefit} className="flex items-center gap-2 text-sm text-zinc-300">
              <Check className="w-4 h-4 text-green-400 flex-shrink-0" aria-hidden="true" />
              {benefit}
            </li>
          ))}
        </ul>

        {/* Pricing */}
        <div className="bg-zinc-800 rounded-lg p-3 mb-4 text-center">
          <p className="text-white font-bold">$9.99/month</p>
          <p className="text-xs text-zinc-500">or $99/year (save $20.88)</p>
        </div>

        {/* CTA */}
        <button
          className="w-full py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-colors text-sm"
        >
          Upgrade to Collector
        </button>

        {/* Dismiss */}
        <button
          onClick={onClose}
          className="w-full py-2 text-sm text-zinc-500 hover:text-zinc-300 transition-colors mt-2"
        >
          Maybe Later
        </button>
      </div>
    </div>
  );
}
